import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import QuickLoader from "@/components/shared/QuickLoader";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import {
  Clock,
  Thermometer,
  SprayCan,
  AlertTriangle,
  ChefHat,
  XCircle,
  Plus,
  Building2,
  LogIn,
  ArrowRight,
  CheckCircle2,
  Bell,
  FileText,
  TrendingUp,
  TrendingDown,
  Activity,
  Bot,
  BarChart2,
} from "lucide-react";

import WeeklyIntelligence from "@/components/dashboard/WeeklyIntelligence";
import InspectionMode from "@/components/dashboard/InspectionMode";
import SecurityAlertsPanel from "@/components/dashboard/SecurityAlertsPanel";
import { PushToggle } from "@/components/PushToggle";
import moment from "moment";
import "moment/locale/ca";

moment.locale("ca");

export default function Dashboard() {
  const { currentVenue, user, userRole, loading: venueLoading } = useVenue();
  const navigate = useNavigate();

  // Workers → redirect to their simplified app
  useEffect(() => {
    if (!venueLoading && currentVenue && userRole === "worker") {
      navigate(createPageUrl("WorkerApp"), { replace: true });
    }
  }, [venueLoading, currentVenue, userRole]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(moment());

  const today = moment().format("YYYY-MM-DD");

  useEffect(() => {
    const interval = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (currentVenue) {
      loadDashboard();
      // Subscribe to TimeRecord changes for real-time updates
      const unsubTimeRecord = hosys.entities.TimeRecord.subscribe((event) => {
        if (event.data?.venue_id === currentVenue.id) {
          loadDashboard();
        }
      });
      return unsubTimeRecord;
    } else {
      setLoading(false);
    }
  }, [currentVenue]);

  const loadDashboard = async () => {
    setLoading(true);
    const venueId = currentVenue.id;
    const [temps, cleaning, incidents, timeRecs, recipes, alerts, docs] = await Promise.all([
      hosys.entities.TemperatureRecord.filter({ venue_id: venueId, date: today }),
      hosys.entities.CleaningRecord.filter({ venue_id: venueId, date: today }),
      hosys.entities.Incident.filter({ venue_id: venueId }),
      hosys.entities.TimeRecord.filter({ venue_id: venueId, date: today }),
      hosys.entities.RecipeSheet.filter({ venue_id: venueId }),
      hosys.entities.Alert.filter({ venue_id: venueId, status: "active" }),
      hosys.entities.Document.filter({ venue_id: venueId }),
    ]);

    const openIncidents = incidents.filter(i => i.status !== "closed");
    const criticalIncidents = openIncidents.filter(i => i.severity === "critical");
    const incorrectTemps = temps.filter(t => t.status === "incorrect");
    const completedCleaning = cleaning.filter(c => c.completed);
    const pendingCleaning = cleaning.filter(c => !c.completed);
    const expiringDocs = docs.filter(d => {
      if (!d.expiry_date) return false;
      const days = moment(d.expiry_date).diff(moment(), "days");
      return days < 0 || days <= 30;
    });
    const criticalAlerts = alerts.filter(a => a.severity === "critical" || a.priority === "critical");
    const entries = timeRecs.filter(r => r.type === "entry");
    const exits = timeRecs.filter(r => r.type === "exit");
    const openShifts = Math.max(0, entries.length - exits.length);

    setData({
      temps, cleaning, incidents, openIncidents, criticalIncidents,
      incorrectTemps, completedCleaning, pendingCleaning,
      timeRecs, recipes, alerts, criticalAlerts, expiringDocs, openShifts,
    });
    setLoading(false);
  };

  if (venueLoading || loading) return <QuickLoader />;

  if (!currentVenue) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-20 h-20 rounded-3xl bg-[#7B2D3F]/10 flex items-center justify-center mb-5">
          <Building2 className="w-9 h-9 text-[#7B2D3F]" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Benvingut a AEH Reus</h2>
        <p className="text-gray-400 mb-6 max-w-sm text-sm">
          No tens cap local assignat. Contacta amb l'administrador o crea'n un.
        </p>
        {(userRole === "superadmin" || userRole === "admin") && (
          <Link to={createPageUrl("AdminVenues")}>
            <Button className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 shadow-md shadow-[#7B2D3F]/20 rounded-xl">
              <Plus className="w-4 h-4" /> Crear local
            </Button>
          </Link>
        )}
      </div>
    );
  }

  const hasAlerts = data?.incorrectTemps?.length > 0 || data?.criticalIncidents?.length > 0 || data?.expiringDocs?.length > 0 || data?.criticalAlerts?.length > 0;
  const cleaningPct = data?.cleaning?.length > 0
    ? Math.round((data.completedCleaning.length / data.cleaning.length) * 100)
    : 0;
  const tempPct = data?.temps?.length > 0
    ? Math.round((data.temps.filter(t => t.status === "correct").length / data.temps.length) * 100)
    : 0;
  const allGood = !hasAlerts && (data?.temps?.length === 0 || tempPct === 100) && (data?.cleaning?.length === 0 || cleaningPct === 100);

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#7B2D3F] mb-1">
            {now.format("dddd, D MMMM YYYY")}
          </p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            Bon dia, {user?.full_name?.split(" ")[0] || "Usuari"} 👋
          </h1>
          <p className="text-gray-400 mt-0.5 text-sm">{currentVenue.name}</p>
        </div>
        <div className="text-right hidden sm:block bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100">
          <p className="text-3xl font-bold text-gray-900 font-mono tabular-nums tracking-tight">{now.format("HH:mm")}</p>
          <p className="text-xs text-gray-400 font-mono">{now.format(":ss")}</p>
          <div className="mt-2">
            <PushToggle />
          </div>
        </div>
      </div>

      {/* ALERTS */}
      {hasAlerts && (
        <div className="space-y-2">
          {data?.criticalAlerts?.length > 0 && (
            <Link to={createPageUrl("NotificationCenter")}>
              <div className="bg-red-600 text-white rounded-2xl p-4 flex items-center gap-3 hover:bg-red-700 transition-colors animate-pulse-subtle shadow-lg shadow-red-600/20">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">{data.criticalAlerts.length} alerta(es) crítiques actives</p>
                  <p className="text-red-100 text-xs mt-0.5">Requereixen atenció immediata</p>
                </div>
                <ArrowRight className="w-4 h-4 opacity-70 flex-shrink-0" />
              </div>
            </Link>
          )}
          {data?.incorrectTemps?.length > 0 && (
            <Link to={createPageUrl("Temperatures")}>
              <div className="bg-orange-500 text-white rounded-2xl p-4 flex items-center gap-3 hover:bg-orange-600 transition-colors shadow-lg shadow-orange-500/20">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Thermometer className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">Temperatura fora de rang</p>
                  <p className="text-orange-100 text-xs mt-0.5">{data.incorrectTemps.length} registre(s) incorrectes avui</p>
                </div>
                <ArrowRight className="w-4 h-4 opacity-70 flex-shrink-0" />
              </div>
            </Link>
          )}
          {data?.criticalIncidents?.length > 0 && (
            <Link to={createPageUrl("Incidents")}>
              <div className="bg-amber-500 text-white rounded-2xl p-4 flex items-center gap-3 hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/20">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">{data.criticalIncidents.length} incidència/es crítiques obertes</p>
                  <p className="text-amber-100 text-xs mt-0.5">Cal atenció urgent</p>
                </div>
                <ArrowRight className="w-4 h-4 opacity-70 flex-shrink-0" />
              </div>
            </Link>
          )}
          {data?.expiringDocs?.length > 0 && (
            <Link to={createPageUrl("Documents")}>
              <div className="bg-violet-600 text-white rounded-2xl p-4 flex items-center gap-3 hover:bg-violet-700 transition-colors shadow-lg shadow-violet-600/20">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">{data.expiringDocs.length} document(s) per renovar</p>
                  <p className="text-violet-100 text-xs mt-0.5">Caducats o pròxims a caducar</p>
                </div>
                <ArrowRight className="w-4 h-4 opacity-70 flex-shrink-0" />
              </div>
            </Link>
          )}
        </div>
      )}

      {allGood && data?.temps?.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="font-semibold text-emerald-800 text-sm">Tot en ordre avui!</p>
            <p className="text-emerald-600 text-xs mt-0.5">Cap incidència ni alerta activa. Excel·lent treball.</p>
          </div>
        </div>
      )}

      {/* Pills */}
      {(data?.pendingCleaning?.length > 0 || data?.openShifts > 0 || data?.openIncidents?.length > 0) && (
        <div className="flex gap-2 flex-wrap">
          {data?.pendingCleaning?.length > 0 && (
            <Link to={createPageUrl("Cleaning")}>
              <span className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-600 rounded-full px-3 py-1.5 text-xs font-semibold hover:border-emerald-300 hover:text-emerald-700 hover:bg-emerald-50 transition-all shadow-sm">
                <SprayCan className="w-3.5 h-3.5" />
                {data.pendingCleaning.length} neteja(es) pendent
              </span>
            </Link>
          )}
          {data?.openShifts > 0 && (
            <Link to={createPageUrl("TimeControl")}>
              <span className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-600 rounded-full px-3 py-1.5 text-xs font-semibold hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50 transition-all shadow-sm">
                <Clock className="w-3.5 h-3.5" />
                {data.openShifts} torn(s) obert
              </span>
            </Link>
          )}
          {data?.openIncidents?.length > 0 && (
            <Link to={createPageUrl("Incidents")}>
              <span className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-600 rounded-full px-3 py-1.5 text-xs font-semibold hover:border-amber-300 hover:text-amber-700 hover:bg-amber-50 transition-all shadow-sm">
                <AlertTriangle className="w-3.5 h-3.5" />
                {data.openIncidents.length} incidència(es) pendent
              </span>
            </Link>
          )}
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-3 gap-3">
        <Link to={createPageUrl("TimeControl")} className="block">
          <div className="bg-[#7B2D3F] hover:bg-[#6B1D2F] text-white rounded-2xl p-4 sm:p-5 text-center transition-all cursor-pointer shadow-lg shadow-[#7B2D3F]/25 hover:shadow-xl hover:shadow-[#7B2D3F]/30 hover:-translate-y-0.5 active:scale-95">
            <LogIn className="w-6 h-6 sm:w-7 sm:h-7 mx-auto mb-1.5 opacity-90" />
            <p className="font-bold text-sm">Fitxar</p>
          </div>
        </Link>
        <Link to={createPageUrl("Temperatures")} className="block">
          <div className="bg-blue-600 hover:bg-blue-700 text-white rounded-2xl p-4 sm:p-5 text-center transition-all cursor-pointer shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/30 hover:-translate-y-0.5 active:scale-95">
            <Thermometer className="w-6 h-6 sm:w-7 sm:h-7 mx-auto mb-1.5 opacity-90" />
            <p className="font-bold text-sm">Temperatura</p>
          </div>
        </Link>
        <Link to={createPageUrl("Cleaning")} className="block">
          <div className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl p-4 sm:p-5 text-center transition-all cursor-pointer shadow-lg shadow-emerald-600/25 hover:shadow-xl hover:shadow-emerald-600/30 hover:-translate-y-0.5 active:scale-95">
            <SprayCan className="w-6 h-6 sm:w-7 sm:h-7 mx-auto mb-1.5 opacity-90" />
            <p className="font-bold text-sm">Neteja</p>
          </div>
        </Link>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          href={createPageUrl("Temperatures")}
          icon={Thermometer}
          label="Temperatures avui"
          value={data?.temps?.length || 0}
          sub={data?.incorrectTemps?.length > 0 ? `${data.incorrectTemps.length} incorrectes` : "Totes correctes"}
          statusOk={!data?.incorrectTemps?.length}
          accentColor="blue"
        />
        <StatCard
          href={createPageUrl("Cleaning")}
          icon={SprayCan}
          label="Neteja avui"
          value={`${data?.completedCleaning?.length || 0}/${data?.cleaning?.length || 0}`}
          sub={`${cleaningPct}% completat`}
          statusOk={cleaningPct === 100}
          accentColor="violet"
          progress={cleaningPct}
        />
        <StatCard
          href={createPageUrl("TimeControl")}
          icon={Clock}
          label="Fitxatges avui"
          value={data?.timeRecs?.length || 0}
          sub={data?.openShifts > 0 ? `${data.openShifts} torn(s) obert` : "Sense torns oberts"}
          statusOk={data?.openShifts === 0}
          accentColor="sky"
        />
        <StatCard
          href={createPageUrl("Incidents")}
          icon={AlertTriangle}
          label="Incidències obertes"
          value={data?.openIncidents?.length || 0}
          sub={data?.openIncidents?.length > 0 ? "Cal atenció" : "Tot en ordre"}
          statusOk={!data?.openIncidents?.length}
          accentColor="amber"
        />
      </div>

      {/* Compliance card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#7B2D3F]/8 bg-[#f9f0f2] flex items-center justify-center">
              <Activity className="w-4.5 h-4.5 text-[#7B2D3F] w-[18px] h-[18px]" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Compliment del dia</h3>
              <p className="text-xs text-gray-400">Resum operatiu diari</p>
            </div>
          </div>
          {data?.cleaning?.length > 0 && cleaningPct === 100 && data?.temps?.length > 0 && !data?.incorrectTemps?.length && (
            <span className="flex items-center gap-1.5 text-emerald-600 text-xs font-bold bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">
              <CheckCircle2 className="w-3.5 h-3.5" /> Excel·lent!
            </span>
          )}
        </div>
        <div className="space-y-4">
          <ComplianceBar
            label="Temperatures correctes"
            done={data?.temps?.filter(t => t.status === "correct").length || 0}
            total={data?.temps?.length || 0}
            color="blue"
          />
          <ComplianceBar
            label="Neteges completades"
            done={data?.completedCleaning?.length || 0}
            total={data?.cleaning?.length || 0}
            color="emerald"
          />
        </div>
        {data?.temps?.length === 0 && data?.cleaning?.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-3">
            Encara no hi ha registres avui.
          </p>
        )}
      </div>

      {/* Inspection Mode */}
      <InspectionMode venueId={currentVenue.id} data={data} today={today} />

      {/* Security alerts panel — superadmin only */}
      {userRole === "superadmin" && <SecurityAlertsPanel />}

      {/* Recipe profitability widget */}
      <RecipeProfitabilityWidget recipes={data?.recipes || []} />

      {/* Weekly Intelligence */}
      <WeeklyIntelligence venueId={currentVenue.id} />

      {/* AI Assistant banner */}
      <AssistantWidget data={data} />

      {/* Quick links */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { page: "Incidents", icon: AlertTriangle, label: "Nova incidència", color: "text-amber-500", bg: "bg-amber-50" },
          { page: "RecipeSheets", icon: ChefHat, label: `Fitxes (${data?.recipes?.length || 0})`, color: "text-orange-500", bg: "bg-orange-50" },
          { page: "Documents", icon: FileText, label: "Documents", color: "text-violet-500", bg: "bg-violet-50" },
          { page: "NotificationCenter", icon: Bell, label: `Alertes ${data?.alerts?.length > 0 ? `(${data.alerts.length})` : ""}`, color: "text-red-500", bg: "bg-red-50" },
          { page: "Downloads", icon: TrendingUp, label: "Generar PDF", color: "text-gray-500", bg: "bg-gray-50" },
        ].map(({ page, icon: Icon, label, color, bg }) => (
          <Link key={page} to={createPageUrl(page)}>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-4 flex items-center gap-3 cursor-pointer">
              <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4.5 h-4.5 ${color} w-[18px] h-[18px]`} />
              </div>
              <span className="text-sm font-semibold text-gray-700">{label}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

const ACCENT_COLORS = {
  blue: { bg: "bg-blue-50", icon: "text-blue-600", dot: "bg-blue-500" },
  violet: { bg: "bg-violet-50", icon: "text-violet-600", dot: "bg-violet-500" },
  sky: { bg: "bg-sky-50", icon: "text-sky-600", dot: "bg-sky-500" },
  amber: { bg: "bg-amber-50", icon: "text-amber-600", dot: "bg-amber-500" },
  emerald: { bg: "bg-emerald-50", icon: "text-emerald-600", dot: "bg-emerald-500" },
};

function StatCard({ href, icon: Icon, label, value, sub, statusOk, accentColor, progress }) {
  const accent = ACCENT_COLORS[accentColor] || ACCENT_COLORS.blue;
  return (
    <Link to={href}>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-4 h-full cursor-pointer">
        <div className="flex items-start justify-between mb-3">
          <div className={`w-9 h-9 rounded-xl ${accent.bg} flex items-center justify-center`}>
            <Icon className={`w-[18px] h-[18px] ${accent.icon}`} />
          </div>
          <span className={`w-2 h-2 rounded-full mt-1 ${statusOk ? "bg-emerald-400" : "bg-red-400"}`} />
        </div>
        <p className="text-2xl font-extrabold text-gray-900 leading-none mb-0.5">{value}</p>
        <p className="text-xs text-gray-400 mb-2">{label}</p>
        {progress !== undefined && (
          <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1.5">
            <div
              className={`h-1.5 rounded-full transition-all duration-500 ${progress === 100 ? "bg-emerald-500" : "bg-[#7B2D3F]"}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
        <p className={`text-xs font-semibold ${statusOk ? "text-emerald-600" : "text-amber-600"}`}>{sub}</p>
      </div>
    </Link>
  );
}

const QUICK_PROMPTS = [
  "Quina temperatura ha de tenir una nevera?",
  "Com actuar davant d'una inspecció sanitària?",
  "Quins al·lèrgens he de controlar?",
  "Cada quant s'ha de netejar la cuina?",
];

function AssistantWidget({ data }) {
  const openAssistant = (question) => {
    window.dispatchEvent(new CustomEvent("open-assistant", question ? { detail: { question } } : undefined));
  };

  const suggestions = [];
  if (data?.incorrectTemps?.length > 0) suggestions.push("Què fer si una temperatura és incorrecta?");
  if (data?.criticalIncidents?.length > 0) suggestions.push("Com gestionar una incidència crítica?");
  if (data?.expiringDocs?.length > 0) suggestions.push("Quins documents necessiten renovació?");

  const prompts = suggestions.length > 0 ? suggestions : QUICK_PROMPTS;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center flex-shrink-0">
          <Bot className="w-5 h-5 text-[#7B2D3F]" />
        </div>
        <div className="flex-1">
          <p className="font-bold text-sm text-gray-900">Assistent IA</p>
          <p className="text-gray-500 text-xs">Expert en hostaleria i seguretat alimentària</p>
        </div>
        <button onClick={() => openAssistant(null)}
          className="text-xs font-semibold text-[#7B2D3F] hover:text-[#5B1D2F] bg-[#7B2D3F]/10 hover:bg-[#7B2D3F]/20 px-3 py-1.5 rounded-xl transition-all flex items-center gap-1">
          Obrir <ArrowRight className="w-3 h-3" />
        </button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {prompts.slice(0, 3).map((q, i) => (
          <button key={`q-${i}`} onClick={() => openAssistant(q)}
            className="flex-shrink-0 text-[11px] font-medium bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-xl px-2.5 py-1.5 transition-colors whitespace-nowrap">
            {q.length > 36 ? q.slice(0, 34) + "…" : q}
          </button>
        ))}
      </div>
    </div>
  );
}

function RecipeProfitabilityWidget({ recipes }) {
  const priced = recipes
    .filter(r => r.selling_price > 0 && r.estimated_cost > 0)
    .map(r => {
      const margin = ((r.selling_price - r.estimated_cost) / r.selling_price) * 100;
      return { ...r, margin };
    });

  if (priced.length === 0) return null;

  const avgMargin = priced.reduce((s, r) => s + r.margin, 0) / priced.length;
  const avgCost = priced.reduce((s, r) => s + r.estimated_cost, 0) / priced.length;
  const lowMargin = priced.filter(r => r.margin < 45);
  const top3 = [...priced].sort((a, b) => b.margin - a.margin).slice(0, 3);
  const marginColor = avgMargin >= 65 ? "text-emerald-600" : avgMargin >= 45 ? "text-amber-600" : "text-red-600";
  const marginBg = avgMargin >= 65 ? "bg-emerald-50 border-emerald-100" : avgMargin >= 45 ? "bg-amber-50 border-amber-100" : "bg-red-50 border-red-100";

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
            <BarChart2 className="w-[18px] h-[18px] text-orange-500" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Rendibilitat de la carta</h3>
            <p className="text-xs text-gray-400">{priced.length} plats analitzats</p>
          </div>
        </div>
        <Link to={createPageUrl("RecipeSheets")}>
          <button className="text-xs font-semibold text-[#7B2D3F] hover:underline flex items-center gap-1">
            Veure anàlisi <ArrowRight className="w-3 h-3" />
          </button>
        </Link>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className={`rounded-xl border p-3 ${marginBg}`}>
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Marge mitjà</p>
          <p className={`text-xl font-extrabold ${marginColor}`}>{avgMargin.toFixed(1)}%</p>
        </div>
        <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Cost mitjà</p>
          <p className="text-xl font-extrabold text-gray-800">{avgCost.toFixed(2)}€</p>
        </div>
        <div className={`rounded-xl border p-3 ${lowMargin.length > 0 ? "bg-red-50 border-red-100" : "bg-emerald-50 border-emerald-100"}`}>
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">A revisar</p>
          <p className={`text-xl font-extrabold ${lowMargin.length > 0 ? "text-red-600" : "text-emerald-600"}`}>{lowMargin.length}</p>
        </div>
      </div>

      {/* Top 3 + low margin */}
      <div className="space-y-1.5">
        {top3.map((r, i) => (
          <div key={r.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">
            <span className="text-[10px] font-bold text-gray-300 w-4 text-center">{i + 1}</span>
            <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
            <span className="flex-1 text-sm font-medium text-gray-700 truncate">{r.name}</span>
            <span className="text-xs font-bold text-emerald-600 flex items-center gap-0.5">
              <TrendingUp className="w-3 h-3" />{r.margin.toFixed(1)}%
            </span>
          </div>
        ))}
        {lowMargin.slice(0, 2).map(r => (
          <div key={r.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg bg-red-50/60">
            <span className="text-[10px] font-bold text-gray-300 w-4 text-center">↓</span>
            <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
            <span className="flex-1 text-sm font-medium text-gray-700 truncate">{r.name}</span>
            <span className="text-xs font-bold text-red-600 flex items-center gap-0.5">
              <TrendingDown className="w-3 h-3" />{r.margin.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ComplianceBar({ label, done, total, color = "emerald" }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const isGood = total === 0 || pct === 100;
  const barColor = isGood ? "bg-emerald-500" : pct > 0 ? "bg-amber-400" : "bg-gray-200";

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm text-gray-600 font-medium">{label}</span>
        <span className={`text-sm font-bold ${isGood ? "text-emerald-600" : pct > 0 ? "text-amber-600" : "text-gray-400"}`}>
          {total === 0 ? "Sense dades" : `${done}/${total}`}
        </span>
      </div>
      {total > 0 && (
        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
          <div
            className={`h-2 rounded-full transition-all duration-700 ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}